<script lang="ts">
	import CheckIcon from '@lucide/svelte/icons/check';
	import LoaderCircleIcon from '@lucide/svelte/icons/loader-circle';
	import CapabilityLogo from '$lib/components/CapabilityLogo.svelte';
	import { presentation } from '$lib/capability-presentation';
	import { onboardingEntries } from '$lib/marketplace-helpers';
	import type { CatalogEntry } from '$lib/marketplace-api';

	type Props = {
		catalog: CatalogEntry[];
		catalogLoading: boolean;
		picked: Set<string>;
		isAdmin: boolean;
		onToggle: (id: string) => void;
		onNext: () => void;
		onBack: () => void;
	};
	// isAdmin is accepted for symmetry with StepTools; the actual entitlement gate
	// is the server's per-user `installable` flag (via onboardingEntries below), so
	// admin-only / restricted / globally-deactivated skills never reach a member.
	let { catalog, catalogLoading, picked, onToggle, onNext, onBack }: Props = $props();

	// Mirror StepTools: only show skills this viewer can actually install. A raw
	// `kind === 'skill'` filter leaked admin-only + active:false skills into a
	// member's onboarding (they're visibleTo=true but installable=false).
	const skills = $derived(onboardingEntries(catalog, 'skill'));

	function handleKeydown(e: KeyboardEvent, id: string): void {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			onToggle(id);
		}
	}
</script>

<div class="flex flex-col gap-9">
	<div class="flex items-baseline justify-between gap-3">
		<div class="flex flex-col gap-1">
			<h2 class="text-[26px] font-medium tracking-[-0.02em] text-[color:var(--fg-strong)]">Skills</h2>
			<p class="text-[12.5px] text-[color:var(--muted-foreground)]">
				Reusable playbooks the assistant follows. One or two is plenty.
			</p>
		</div>
		<span class="shrink-0 text-[12px] tabular-nums text-[color:var(--muted-foreground)]">
			<strong class="font-medium text-[color:var(--fg-strong)]">{picked.size}</strong> picked
		</span>
	</div>

	{#if catalogLoading}
		<div class="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
			{#each Array(3) as _, i (i)}
				<div
					class="h-[92px] animate-pulse rounded-[14px] border"
					style="background: var(--glass); border-color: var(--glass-border);"
				></div>
			{/each}
		</div>
	{:else}
		<div class="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
			{#each skills as entry (entry.id)}
				{@const isPicked = picked.has(entry.id)}
				{@const pres = presentation(entry.id, entry.latestVersion)}
				<div
					role="button"
					tabindex={0}
					aria-pressed={isPicked}
					onclick={() => onToggle(entry.id)}
					onkeydown={(e) => handleKeydown(e, entry.id)}
					class="group relative cursor-pointer rounded-[14px] border p-[18px] transition-all focus:outline-none"
					style="
						background: {isPicked
						? 'linear-gradient(180deg, color-mix(in srgb, var(--primary) 6%, transparent), color-mix(in srgb, var(--primary) 2%, transparent)), var(--glass)'
						: 'var(--glass)'};
						border-color: {isPicked
						? 'color-mix(in srgb, var(--primary) 45%, transparent)'
						: 'var(--glass-border)'};
						backdrop-filter: blur(18px) saturate(130%);
						-webkit-backdrop-filter: blur(18px) saturate(130%);
						box-shadow: {isPicked
						? '0 0 0 1px color-mix(in srgb, var(--primary) 18%, transparent) inset, 0 18px 50px -22px var(--glow), 0 0 60px -20px var(--glow)'
						: 'none'};
					"
				>
					<div class="flex items-center gap-3 pr-7">
						<CapabilityLogo brand={pres?.brand ?? null} mono />
						<div class="min-w-0 flex-1">
							<p class="m-0 truncate text-[14px] font-medium tracking-[-0.01em] text-[color:var(--fg-strong)]">
								{entry.name}
							</p>
							<p class="mt-0.5 truncate text-[12px] text-[color:var(--muted-foreground)]">
								{pres?.essence ?? entry.summary}
							</p>
						</div>
					</div>

					{#if entry.recommended && !isPicked}
						<span
							class="pointer-events-none absolute right-[15px] top-[15px] size-[5px] rounded-full"
							style="background: var(--fg-faint); opacity: 0.5;"
							aria-hidden="true"
						></span>
					{/if}
					<span
						class="pointer-events-none absolute right-[14px] top-[14px] grid size-[18px] place-items-center rounded-full bg-primary text-primary-foreground"
						style="
							transform: scale({isPicked ? 1 : 0});
							transition: transform .25s cubic-bezier(.4,1.6,.4,1);
							box-shadow: 0 0 18px var(--glow);
						"
						aria-hidden="true"
					>
						<CheckIcon class="size-[10px]" strokeWidth={3} />
					</span>
				</div>
			{/each}
		</div>
	{/if}

	<div class="flex items-center justify-between gap-3 pt-1">
		<span class="text-[12px] text-[color:var(--fg-faint)]">Zero is fine.</span>
		<div class="flex items-center gap-2">
			<button
				type="button"
				onclick={onBack}
				class="inline-flex items-center justify-center rounded-full border px-5 py-2 text-[13px] font-medium text-[color:var(--muted-foreground)] backdrop-blur-md transition-colors hover:text-[color:var(--fg-strong)]"
				style="background: var(--glass); border-color: var(--glass-border);"
			>
				Back
			</button>
			<button
				type="button"
				onclick={onNext}
				disabled={catalogLoading}
				class="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-2 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-[color:var(--primary)]/90 disabled:opacity-60"
				style="box-shadow: 0 8px 28px -10px var(--glow), 0 0 0 1px rgba(255,255,255,0.06) inset;"
			>
				{#if catalogLoading}
					<LoaderCircleIcon class="size-3.5 animate-spin" />
				{/if}
				Continue
			</button>
		</div>
	</div>
</div>
